import { useMemo } from 'react';
import { compact, noop } from 'lodash';

import {
   Button,
   ButtonVariant,
   createDynamicColumnsWidth,
   JustifyContent,
   StaticDataConfiguration,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';
import { useTranslate } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';
import { useResourceBranchByHealthcareProviderType } from '~libs.shared.account';

import { statisticsTranslationKeys } from '../../translations';

import { GenerateStatisticsModalOpener } from './GenerateStatisticsModal.opener';
import { StatisticsCode, StatisticsListType } from './StatisticsListTable.types';
import { NameCell } from './cells/NameCell';

export const useStatisticsListColumns = (): TableColumnConfiguration<StatisticsListType>[] => {
   const columnsWidth = createDynamicColumnsWidth({
      name: '100%',
      actions: '130px',
   });

   return useMemo<TableColumnConfiguration<StatisticsListType>[]>(
      () => [
         {
            name: 'name',
            accessor: 'name',
            label: statisticsTranslationKeys.statisticsListTableNameHeader,
            width: columnsWidth.name,
            renderer: ({ bean }: ValueRendererProps<StatisticsListType>) => (
               <NameCell name={bean.name} description={bean.description} />
            ),
            sortingDisabled: true,
         },
         {
            name: 'actions',
            accessor: noop,
            width: columnsWidth.actions,
            justifyContent: JustifyContent.END,
            renderer: ({ bean }: ValueRendererProps<StatisticsListType>) => (
               <GenerateStatisticsModalOpener code={bean.code}>
                  {({ openModal }) => (
                     <Button
                        label={statisticsTranslationKeys.generateStatisticsButtonLabel}
                        variant={ButtonVariant.SECONDARY}
                        onClick={openModal}
                        testId={'generateStatisticsButton'}
                     />
                  )}
               </GenerateStatisticsModalOpener>
            ),
            sortingDisabled: true,
         },
      ],
      [columnsWidth]
   );
};

export const useStatisticsListDataConfiguration = (): StaticDataConfiguration<StatisticsListType> => {
   const translate = useTranslate();
   const zd3StatisticConfiguration = useResourceBranchByHealthcareProviderType(
      {
         name: translate(statisticsTranslationKeys.modal.type.healthcareProviderZD3),
         code: 'HealthcareProviderCreateZD3ReportStatistics' as StatisticsCode,
         description: '',
      },
      null
   );

   const configuration: StatisticsListType[] = useMemo(() => {
      const nameGroup = statisticsTranslationKeys.modal.type;

      return compact([
         {
            name: translate(nameGroup.doctorDailyVisit),
            code: 'DoctorDailyVisitStatistics',
            description: '',
         },
         {
            name: translate(nameGroup.doctorDailyMainDiagnosis),
            code: 'DoctorDailyMainDiagnosisStatistics',
            description: '',
         },
         {
            name: translate(nameGroup.doctorDailyCoexistentDiagnosis),
            code: 'DoctorDailyCoexistentDiagnosisStatistics',
            description: '',
         },
         {
            name: translate(nameGroup.healthcarePractitionerDailyAppointment),
            code: 'HealthcarePractitionerCreateDailyAppointmentReport',
            description: '',
         },
         {
            name: translate(nameGroup.healthcareProviderDailyVisit),
            code: 'HealthcareProviderDailyVisitStatistics',
            description: '',
         },
         zd3StatisticConfiguration,
         {
            name: translate(statisticsTranslationKeys.modal.type.healthcareProviderMZ15),
            code: 'HealthcareProviderCreateMZ15ReportStatistics' as StatisticsCode,
            description: '',
         },
         protectFeature('managingPrivateMedicalServices', {
            name: translate(nameGroup.healthcarePractitionerSettlementDetailed),
            code: 'HealthcarePractitionerCreateSettlementDetailedStatistics',
            description: '',
         }),
         protectFeature('managingPrivateMedicalServices', {
            name: translate(nameGroup.healthcarePractitionerSettlementGeneral),
            code: 'HealthcarePractitionerCreateSettlementGeneralStatistics',
            description: '',
         }),
         protectFeature('managingPrivateMedicalServices', {
            name: translate(nameGroup.cashReport),
            code: 'HealthcarePractitionerCashReportStatistics',
            description: '',
         }),
         protectFeature(['creatingStatistics', 'searchingMedicalData'], {
            name: translate(nameGroup.healthcareProviderPatientsWithDiagnosis),
            code: 'HealthcareProviderPatientsWithDiagnosisStatistics',
            description: '',
         }),
      ]);
   }, [translate, zd3StatisticConfiguration]);

   return {
      data: configuration,
   };
};
